/**
 * Control Room → Drift (sak 238, jobb 5): `GET /v1/platform/operations`.
 *
 * Fire ting om miljøet API-et kjører i, og ingenting om en kunde:
 *
 * 1. `checks` — svarer hver avhengighet, og hvor fort. Hver sjekk har sin egen tidsgrense og kjøres
 *    parallelt; en sjekk som henger eller kaster blir `fail`, aldri en feil på hele svaret.
 *    `not_connected` er en tredje sannhet: adapteren som er valgt, ser ikke på noe (skanneren `noop`,
 *    e-post `fake`, SMS `disabled`) — eller meldingslaget finnes ikke i prosessen.
 * 2. `responseTimes` — API-ets egne målinger, per time det siste døgnet, i minnet. Nullstilles ved
 *    hver utrulling (`since`). `watchdog` er vaktas syn utenfra (Cloudflare-workeren «tvitvi-vakt»),
 *    hentet av API-et, aldri av nettleseren.
 * 3. `deployment` — commit og utrulling fra Railways egne variabler; `local` uten dem. Railway har
 *    ingen variabel for når utrullingen ble laget, så tiden er når prosessen startet.
 * 4. `lastBackup` — siste linje i `BACKUP_LOG_PATH` (K6 L11: bare tid, status og størrelse — aldri
 *    filstien). Null når variabelen ikke er satt.
 */
import { z } from 'zod';

export const platformLastBackupSchema = z
  .object({
    /** Tidspunktet på linja, eller null når ingen linje kunne leses. */
    at: z.iso.datetime().nullable(),
    ok: z.boolean(),
    /** Størrelsen på kopien, lest av «(4,7 MB, …)». Null når linja ikke har den (en feilet kopi). */
    sizeBytes: z.number().int().nonnegative().nullable(),
  })
  .meta({ id: 'PlatformLastBackup' });

export const PLATFORM_OPERATIONS_CHECKS = [
  'api',
  'database',
  'storage',
  'scanner',
  'email',
  'sms',
] as const;
export type PlatformOperationsCheckName = (typeof PLATFORM_OPERATIONS_CHECKS)[number];

/** En maskinlesbar kode, aldri en setning og aldri en vert eller en nøkkel. */
const codeSchema = z
  .string()
  .max(40)
  .regex(/^[a-z0-9_]+$/);

export const platformOperationsCheckSchema = z
  .object({
    name: z.enum(PLATFORM_OPERATIONS_CHECKS),
    status: z.enum(['ok', 'fail', 'not_connected']),
    /** Hvor lenge sjekken tok. Null når ingenting ble spurt (`not_connected` uten adapter). */
    latencyMs: z.number().int().nonnegative().nullable(),
    /** Adapteren som er valgt: `local`/`r2`, `noop`/`clamav`, `brevo`/`fake`, `disabled`. */
    driver: codeSchema.nullable(),
    /** Hvorfor den feilet: `timeout`, `error`, `unauthorized`, `network`, `provider_503` … */
    reason: codeSchema.nullable(),
    /** Skannerens versjon og signaturdato («ClamAV 1.4.1/27412/…»), når den svarer. */
    version: z.string().max(120).nullable(),
  })
  .meta({ id: 'PlatformOperationsCheck' });

export const platformResponseTimeHourSchema = z
  .object({
    /** Timens start (hel klokketime, UTC). */
    hour: z.iso.datetime(),
    count: z.number().int().nonnegative(),
    /** Null når timen ikke har en eneste forespørsel. */
    p50Ms: z.number().int().nonnegative().nullable(),
    p95Ms: z.number().int().nonnegative().nullable(),
  })
  .meta({ id: 'PlatformResponseTimeHour' });

export const platformWatchdogSchema = z
  .object({
    /** `not_configured` uten `VAKT_STATUS_URL`; `unreachable` når vakta ikke svarte med noe lesbart. */
    state: z.enum(['ok', 'not_configured', 'unreachable']),
    down: z.boolean().nullable(),
    downSince: z.iso.datetime().nullable(),
    failures: z.number().int().nonnegative().nullable(),
    checkedAt: z.iso.datetime().nullable(),
    probes: z.array(
      z.object({
        name: codeSchema,
        ok: z.boolean(),
        status: z.number().int().nonnegative(),
        ms: z.number().int().nonnegative(),
      }),
    ),
    /** Siste varsel vakta prøvde å sende, og om Brevo tok imot det. */
    lastAlert: z.object({ at: z.iso.datetime(), sent: z.boolean() }).nullable(),
  })
  .meta({ id: 'PlatformWatchdog' });

export const platformOperationsResponseSchema = z
  .object({
    checks: z.array(platformOperationsCheckSchema),
    responseTimes: z.object({
      /** Når målingen startet: prosessens oppstart. Alt før er borte. */
      since: z.iso.datetime(),
      /** De siste 24 klokketimene, eldste først; den siste er timen som pågår. */
      hours: z.array(platformResponseTimeHourSchema),
      total: z.object({
        count: z.number().int().nonnegative(),
        p50Ms: z.number().int().nonnegative().nullable(),
        p95Ms: z.number().int().nonnegative().nullable(),
      }),
    }),
    watchdog: platformWatchdogSchema,
    deployment: z.object({
      kind: z.enum(['railway', 'local']),
      commitSha: z.string().max(64).nullable(),
      branch: z.string().max(200).nullable(),
      deploymentId: z.string().max(64).nullable(),
      environment: z.string().max(100).nullable(),
      /** Når prosessen startet — det nærmeste Railway lar oss vite om når utrullingen gikk. */
      startedAt: z.iso.datetime(),
    }),
    lastBackup: platformLastBackupSchema.nullable(),
  })
  .meta({ id: 'PlatformOperationsResponse' });

export type PlatformLastBackup = z.infer<typeof platformLastBackupSchema>;
export type PlatformOperationsCheck = z.infer<typeof platformOperationsCheckSchema>;
export type PlatformResponseTimeHour = z.infer<typeof platformResponseTimeHourSchema>;
export type PlatformWatchdog = z.infer<typeof platformWatchdogSchema>;
export type PlatformOperationsResponse = z.infer<typeof platformOperationsResponseSchema>;
